import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCw, X } from 'lucide-react';

const STORAGE_KEY = 'bookingLandscapeHintDismissed';

interface LandscapeHintProps {
  /** Show only on dense steps (calendar / slots). */
  active: boolean;
}

/**
 * Soft tip for short portrait phones on calendar/slot steps.
 * Dismissible for the session; never blocks the flow.
 */
export function LandscapeHint({ active }: LandscapeHintProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return;

    const mq = window.matchMedia('(orientation: portrait) and (max-width: 767px)');
    const sync = () => setVisible(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [active]);

  if (!visible) return null;

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  return (
    <div
      role="status"
      className="md:hidden mb-4 flex items-start gap-3 rounded-lg border border-primary-300/40 bg-primary-50 px-3 py-2.5 text-sm text-textPrimary"
    >
      <RotateCw className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-500" aria-hidden />
      <p className="flex-1 leading-snug">{t('bookingUi.landscapeHint')}</p>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 rounded text-textSecondary hover:text-textPrimary"
        aria-label={t('common.close')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
